import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Match, GameAnswer } from '../types';
import QuestionCard from './QuestionCard';
import Timer from './Timer';

interface MultiplayerGameProps {
  match: Match;
  isPlayer1: boolean;
  onAnswerSubmit: (answer: GameAnswer) => void;
  onForfeit: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const ScoreboardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const PlayerCard = styled(motion.div)<{ $isCurrentPlayer: boolean; $isWinning?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme, $isCurrentPlayer, $isWinning }) => {
    if ($isWinning) return theme.colors.success + '15';
    if ($isCurrentPlayer) return theme.colors.primary + '20';
    return 'transparent';
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme, $isCurrentPlayer, $isWinning }) => {
    if ($isWinning) return theme.colors.success;
    if ($isCurrentPlayer) return theme.colors.primary;
    return theme.colors.surface;
  }};
  box-shadow: ${({ $isWinning, theme }) =>
    $isWinning ? `0 0 20px ${theme.colors.success}40` : 'none'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ theme, $isWinning }) =>
      $isWinning
        ? `linear-gradient(90deg, ${theme.colors.success}, ${theme.colors.secondary})`
        : 'transparent'};
    opacity: ${({ $isWinning }) => ($isWinning ? 1 : 0)};
    transition: opacity 0.3s ease;
  }
`;

const PlayerName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 0.9rem;
  }
`;

const PlayerScore = styled(motion.div)`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.secondary}, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 10px ${({ theme }) => theme.colors.secondary}30;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 2rem;
  }
`;

const StatusBadge = styled.div<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $connected }) =>
    $connected ? theme.colors.success + '30' : theme.colors.error + '30'};
  color: ${({ theme, $connected }) =>
    $connected ? theme.colors.success : theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
`;

const ActivityIndicator = styled(motion.div)<{ $status: 'thinking' | 'answered' | 'waiting' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $status }) => {
    if ($status === 'answered') return theme.colors.info + '30';
    if ($status === 'thinking') return theme.colors.warning + '30';
    return theme.colors.textSecondary + '20';
  }};
  color: ${({ theme, $status }) => {
    if ($status === 'answered') return theme.colors.info;
    if ($status === 'thinking') return theme.colors.warning;
    return theme.colors.textSecondary;
  }};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
  min-height: 24px;
`;

const VersusText = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};

  ${({ theme }) => theme.breakpoints.mobile} {
    display: none;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ProgressText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 0.9rem;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.secondary}
  );
  border-radius: ${({ theme }) => theme.borderRadius.full};
  box-shadow: 0 0 10px ${({ theme }) => theme.colors.primary}60;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const QuestionContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const TimerContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const Button = styled(motion.button)<{ $variant?: 'primary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  background: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.error : theme.colors.primary};
  color: #fff;
  box-shadow: ${({ theme }) => theme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 0.9rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const WaitingMessage = styled(motion.div)`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

const DisconnectOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const DisconnectCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;
  max-width: 400px;
  margin: ${({ theme }) => theme.spacing.lg};
`;

const DisconnectTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.warning};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DisconnectMessage = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const DisconnectTimer = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const MultiplayerGame: React.FC<MultiplayerGameProps> = ({
  match,
  isPlayer1,
  onAnswerSubmit,
  onForfeit,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [disconnectCountdown, setDisconnectCountdown] = useState<number | null>(null);

  const currentPlayer = isPlayer1 ? match.player1 : match.player2;
  const opponent = isPlayer1 ? match.player2 : match.player1;
  const currentQuestion = match.questions[match.currentQuestionIndex];
  const progress = ((match.currentQuestionIndex + 1) / match.questions.length) * 100;

  // Track opponent disconnect status
  const opponentDisconnected = opponent ? !opponent.connected : false;

  // Reset state when question changes
  useEffect(() => {
    setSelectedAnswer(null);
    setShowResult(false);
    setTimeLeft(15);
    setHasAnswered(false);
  }, [match.currentQuestionIndex]);

  // Check if opponent has answered
  const opponentHasAnswered = opponent
    ? opponent.answers.length > match.currentQuestionIndex
    : false;

  // Get opponent's selected answer for current question
  const opponentSelectedAnswer = opponent && opponentHasAnswered
    ? opponent.answers[match.currentQuestionIndex]?.selectedAnswer ?? null
    : null;

  // Handle answer selection
  const handleAnswerSelect = (answerIndex: number) => {
    if (hasAnswered || showResult || !currentQuestion) return;

    setSelectedAnswer(answerIndex);
    setShowResult(true);
    setHasAnswered(true);

    const isCorrect = answerIndex === currentQuestion.correctAnswer;
    const timeSpent = 15 - timeLeft;
    const points = calculatePoints(isCorrect, timeSpent, currentQuestion.difficulty);

    const answer: GameAnswer = {
      questionId: currentQuestion.id,
      selectedAnswer: answerIndex,
      isCorrect,
      timeSpent: timeSpent * 1000, // Convert to milliseconds
      points,
    };

    onAnswerSubmit(answer);
  };

  // Calculate points based on correctness, time, and difficulty
  const calculatePoints = (
    isCorrect: boolean,
    timeSpent: number,
    difficulty: string
  ): number => {
    if (!isCorrect) return 0;

    const basePoints = {
      easy: 100,
      medium: 200,
      hard: 300,
      expert: 500,
    }[difficulty] || 100;

    const timeBonus = Math.max(0, Math.floor((15 - timeSpent) * 10));
    return basePoints + timeBonus;
  };

  // Timer countdown
  useEffect(() => {
    if (hasAnswered || showResult) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [hasAnswered, showResult]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (timeLeft === 0 && !hasAnswered) {
      handleAnswerSelect(-1); // Submit invalid answer
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, hasAnswered]);

  // Disconnect countdown timer (30 seconds)
  useEffect(() => {
    if (opponentDisconnected) {
      setDisconnectCountdown(30);

      const interval = setInterval(() => {
        setDisconnectCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setDisconnectCountdown(null);
    }
  }, [opponentDisconnected]);

  // If we've advanced past the last question, show waiting message
  if (!currentQuestion || match.currentQuestionIndex >= match.questions.length) {
    return (
      <Container>
        <WaitingMessage
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🏁</div>
          <div>Match complete! Calculating results...</div>
        </WaitingMessage>
      </Container>
    );
  }

  return (
    <Container>
      {/* Scoreboard */}
      <ScoreboardContainer>
        <PlayerCard
          $isCurrentPlayer={true}
          $isWinning={currentPlayer && opponent ? currentPlayer.score > opponent.score : false}
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
        >
          <PlayerName>You</PlayerName>
          <PlayerScore
            key={`player-score-${currentPlayer?.score}`}
            initial={{ scale: 1, rotateY: 0 }}
            animate={{
              scale: [1, 1.3, 1],
              rotateY: [0, 360, 0]
            }}
            transition={{
              duration: 0.5,
              ease: "easeOut"
            }}
          >
            {currentPlayer?.score || 0}
          </PlayerScore>
          <StatusBadge $connected={currentPlayer?.connected || false}>
            {currentPlayer?.connected ? '🟢 Connected' : '🔴 Disconnected'}
          </StatusBadge>
          <ActivityIndicator
            $status={hasAnswered ? 'answered' : 'thinking'}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {hasAnswered ? '✓ Answered!' : '⏱️ Thinking...'}
          </ActivityIndicator>
        </PlayerCard>

        <VersusText>VS</VersusText>

        <PlayerCard
          $isCurrentPlayer={false}
          $isWinning={currentPlayer && opponent ? opponent.score > currentPlayer.score : false}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
        >
          <PlayerName>{opponent?.displayName || 'Opponent'}</PlayerName>
          <PlayerScore
            key={`opponent-score-${opponent?.score}`}
            initial={{ scale: 1, rotateY: 0 }}
            animate={{
              scale: [1, 1.3, 1],
              rotateY: [0, 360, 0]
            }}
            transition={{
              duration: 0.5,
              ease: "easeOut"
            }}
          >
            {opponent?.score || 0}
          </PlayerScore>
          <StatusBadge $connected={opponent?.connected || false}>
            {opponent?.connected ? '🟢 Connected' : '🔴 Disconnected'}
          </StatusBadge>
          <ActivityIndicator
            $status={opponentHasAnswered ? 'answered' : 'thinking'}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            key={`opponent-activity-${match.currentQuestionIndex}-${opponentHasAnswered}`}
          >
            {opponentHasAnswered ? '✓ Answered!' : '⏱️ Thinking...'}
          </ActivityIndicator>
        </PlayerCard>
      </ScoreboardContainer>

      {/* Progress */}
      <ProgressContainer>
        <ProgressText>
          Question {match.currentQuestionIndex + 1} / {match.questions.length}
        </ProgressText>
        <ProgressBar>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </ProgressBar>
      </ProgressContainer>

      {/* Question */}
      <QuestionContainer>
        <TimerContainer>
          <Timer timeLeft={timeLeft} totalTime={15} />
        </TimerContainer>

        <AnimatePresence mode="wait">
          <motion.div
            key={match.currentQuestionIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            <QuestionCard
              question={currentQuestion}
              selectedAnswer={selectedAnswer}
              onAnswerSelect={handleAnswerSelect}
              showResult={showResult}
              disabled={hasAnswered}
              opponentAnswer={opponentSelectedAnswer}
            />
          </motion.div>
        </AnimatePresence>

        {hasAnswered && !opponentHasAnswered && (
          <WaitingMessage
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Waiting for opponent to answer...
          </WaitingMessage>
        )}

        <ActionButtons>
          <Button
            $variant="danger"
            onClick={onForfeit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Forfeit Match
          </Button>
        </ActionButtons>
      </QuestionContainer>

      {/* Disconnect Overlay */}
      <AnimatePresence>
        {opponentDisconnected && disconnectCountdown !== null && (
          <DisconnectOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <DisconnectCard
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <DisconnectTitle>⚠️ Opponent Disconnected</DisconnectTitle>
              <DisconnectMessage>
                {opponent?.displayName || 'Your opponent'} has lost connection.
              </DisconnectMessage>
              <DisconnectMessage>
                Waiting for them to reconnect...
              </DisconnectMessage>
              <DisconnectTimer>{disconnectCountdown}s</DisconnectTimer>
              <DisconnectMessage style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                Match will be forfeited if they don't reconnect in time.
              </DisconnectMessage>
            </DisconnectCard>
          </DisconnectOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default MultiplayerGame;
